/**
 * 画布自动整理布局算法
 *
 * DAG 分层布局（Sugiyama 框架简化版）：
 *  1. 根据连线构建有向无环图
 *  2. 拓扑排序确定每个节点的层（列）
 *  3. barycenter 启发式重排层内顺序，减少连线交叉
 *  4. 逐列分配 y：节点拉向所有上游邻居的中心，使同一编排链尽量落在同一排
 *  5. 列内去重叠（自上而下推开 + 自下而上回推，保持 barycenter 序）
 *  6. 孤立节点（无连线的）单独放在最右侧
 */
import type { CanvasNode, CanvasEdge } from './types';
import { DEFAULT_NODE_SIZES } from './constants';

const GAP_X = 80; // 列间距
const GAP_Y = 40; // 同行内节点间距

/** barycenter 迭代轮数（向下 + 向上各算一轮，3 轮基本收敛） */
const BARYCENTER_ROUNDS = 3;

interface LayoutResult {
  id: string;
  x: number;
  y: number;
}

/**
 * 计算自动布局后的节点位置
 */
export function computeAutoLayout(nodes: CanvasNode[], edges: CanvasEdge[]): LayoutResult[] {
  if (nodes.length === 0) return [];

  // 邻接表：outgoing[nodeId] = [targetNodeId, ...]
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  for (const n of nodes) {
    outgoing.set(n.id, []);
    incoming.set(n.id, []);
  }
  for (const e of edges) {
    // 只处理画布内存在的节点之间的边
    if (outgoing.has(e.sourceNodeId) && incoming.has(e.targetNodeId)) {
      outgoing.get(e.sourceNodeId)!.push(e.targetNodeId);
      incoming.get(e.targetNodeId)!.push(e.sourceNodeId);
    }
  }

  // 分离孤立节点（无入边也无出边），单独排到最右侧
  const isolatedIds = new Set<string>();
  const layoutNodes: CanvasNode[] = [];
  for (const n of nodes) {
    if (incoming.get(n.id)!.length === 0 && outgoing.get(n.id)!.length === 0) {
      isolatedIds.add(n.id);
    } else {
      layoutNodes.push(n);
    }
  }
  const isolatedNodes = nodes.filter((n) => isolatedIds.has(n.id));

  // 全部是孤立节点：直接纵向排列
  if (layoutNodes.length === 0) {
    return layoutIsolatedOnly(nodes);
  }

  // 分层：从根节点（无入边）开始 BFS，每节点的层 = max(所有前驱层) + 1
  const layerOf = new Map<string, number>();

  // 拓扑排序（Kahn 算法）
  const inDeg = new Map<string, number>();
  for (const n of layoutNodes) {
    inDeg.set(n.id, incoming.get(n.id)!.length);
  }
  const queue: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) {
      layerOf.set(id, 0);
      queue.push(id);
    }
  }

  const visited = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const myLayer = layerOf.get(id) ?? 0;

    for (const target of outgoing.get(id) || []) {
      const nextLayer = Math.max(layerOf.get(target) ?? 0, myLayer + 1);
      layerOf.set(target, nextLayer);
      inDeg.set(target, (inDeg.get(target) ?? 0) - 1);
      if (inDeg.get(target) === 0) {
        queue.push(target);
      }
    }
  }

  // 处理环中未访问的节点（给它们默认层）
  for (const n of layoutNodes) {
    if (!layerOf.has(n.id)) {
      layerOf.set(n.id, 0);
    }
  }

  // 按层分组
  const layers = new Map<number, CanvasNode[]>();
  for (const n of layoutNodes) {
    const layer = layerOf.get(n.id)!;
    if (!layers.has(layer)) layers.set(layer, []);
    layers.get(layer)!.push(n);
  }

  // 层内初始排序：按原始 y 坐标（保持用户大致摆放顺序）
  for (const [, layerNodes] of layers) {
    layerNodes.sort((a, b) => a.y - b.y);
  }

  // barycenter 启发式：减少连线交叉
  const sortedLayerKeys = [...layers.keys()].sort((a, b) => a - b);
  applyBarycenter(layers, sortedLayerKeys, outgoing, incoming);

  // 计算每个节点的位置
  const result: LayoutResult[] = [];

  // 找到画布起始坐标
  const allX = nodes.map((n) => n.x);
  const allY = nodes.map((n) => n.y);
  const minCanvasX = Math.min(...allX);
  const minCanvasY = Math.min(...allY);

  // 节点实际尺寸（节点可被 resize、高度由 ResizeObserver 回写），
  // 默认尺寸表仅作缺失兜底——用默认值排布会把调高过的节点排重叠
  const sizeOf = (n: CanvasNode) => ({
    width: n.width || (DEFAULT_NODE_SIZES[n.type]?.width ?? 320),
    height: n.height || (DEFAULT_NODE_SIZES[n.type]?.height ?? 220),
  });

  // 预计算各层（列）的最大宽，用于 x 推进
  const columnWidths = sortedLayerKeys.map((key) => {
    const layerNodes = layers.get(key)!;
    let maxWidth = 0;
    for (const n of layerNodes) {
      maxWidth = Math.max(maxWidth, sizeOf(n).width);
    }
    return maxWidth;
  });

  // y 坐标分配：逐列处理。
  // 第一列从 minCanvasY 自上而下堆叠；后续列每个节点拉向所有上游邻居的中心 y，
  // 使同一编排链尽量落在同一排，再用"推开 + 回推"消除列内重叠。
  const yOf = new Map<string, number>(); // 最终 y（节点顶部坐标）

  for (let li = 0; li < sortedLayerKeys.length; li++) {
    const layerNodes = layers.get(sortedLayerKeys[li])!;
    const positioned: { id: string; y: number; h: number }[] = [];

    for (const n of layerNodes) {
      const h = sizeOf(n).height;
      let y: number;
      if (li === 0) {
        // 第一列：无上游，用原始 y 作为期望值，后续由推开 + 下移兜底对齐
        y = n.y;
      } else {
        // 拉向所有上游邻居的中心：期望 y = avg(上游中心) - 自身高度/2
        const ups = incoming.get(n.id) || [];
        const upCenters = ups
          .map((uid) => {
            const uy = yOf.get(uid);
            if (uy === undefined) return undefined;
            const uh = sizeOf(nodes.find((nn) => nn.id === uid)!).height;
            return uy + uh / 2;
          })
          .filter((v): v is number => v !== undefined);
        y = upCenters.length > 0
          ? upCenters.reduce((a, b) => a + b, 0) / upCenters.length - h / 2
          : n.y;
      }
      positioned.push({ id: n.id, y, h });
    }

    // 列内按 barycenter 序（positioned 已按 layers 顺序，即 barycenter 后的序）
    // 自上而下推开：保证每个节点顶部 >= 上一个节点底部 + GAP_Y
    for (let i = 1; i < positioned.length; i++) {
      const prevBottom = positioned[i - 1].y + positioned[i - 1].h;
      if (positioned[i].y < prevBottom + GAP_Y) {
        positioned[i].y = prevBottom + GAP_Y;
      }
    }
    // 自下而上回推：如果某节点被推太远，把上方节点也拉下来避免大空洞（3 轮收敛）
    for (let round = 0; round < 3; round++) {
      for (let i = positioned.length - 2; i >= 0; i--) {
        const nextTop = positioned[i + 1].y;
        if (positioned[i].y + positioned[i].h + GAP_Y > nextTop) {
          positioned[i].y = nextTop - positioned[i].h - GAP_Y;
        }
      }
      for (let i = 1; i < positioned.length; i++) {
        const prevBottom = positioned[i - 1].y + positioned[i - 1].h;
        if (positioned[i].y < prevBottom + GAP_Y) {
          positioned[i].y = prevBottom + GAP_Y;
        }
      }
    }

    // 整列下移：确保不超出画布顶部（minCanvasY）
    const minY = positioned.reduce((m, p) => Math.min(m, p.y), Infinity);
    const shift = minY < minCanvasY ? minCanvasY - minY : 0;
    for (const p of positioned) {
      yOf.set(p.id, p.y + shift);
    }
  }

  // 写出 x / y
  let curX = minCanvasX;
  for (let li = 0; li < sortedLayerKeys.length; li++) {
    const layerNodes = layers.get(sortedLayerKeys[li])!;
    for (const n of layerNodes) {
      result.push({ id: n.id, x: curX, y: yOf.get(n.id)! });
    }
    curX += columnWidths[li] + GAP_X;
  }

  // 主布局 y 范围（用于孤立节点居中参考）
  let mainMinY = Infinity;
  let mainMaxY = -Infinity;
  for (const n of layoutNodes) {
    const y = yOf.get(n.id);
    if (y === undefined) continue;
    mainMinY = Math.min(mainMinY, y);
    mainMaxY = Math.max(mainMaxY, y + sizeOf(n).height);
  }
  if (!isFinite(mainMinY)) mainMinY = minCanvasY;
  if (!isFinite(mainMaxY)) mainMaxY = minCanvasY;
  const mainCenterY = (mainMinY + mainMaxY) / 2;

  // 孤立节点排在最右侧，以主布局中心垂直居中
  if (isolatedNodes.length > 0) {
    isolatedNodes.sort((a, b) => a.y - b.y);
    let isolatedTotal = 0;
    for (const n of isolatedNodes) isolatedTotal += sizeOf(n).height;
    isolatedTotal += Math.max(0, isolatedNodes.length - 1) * GAP_Y;
    let curY = mainCenterY - isolatedTotal / 2;
    for (const n of isolatedNodes) {
      result.push({ id: n.id, x: curX, y: curY });
      curY += sizeOf(n).height + GAP_Y;
    }
  }

  return result;
}

/**
 * barycenter 启发式：通过层间信息传播重排层内顺序，减少连线交叉。
 *
 * 向下扫描时用上一层的节点序号计算 barycenter；
 * 向上扫描时用下一层的节点序号计算 barycenter。
 * barycenter 相同时保持原顺序（稳定排序），避免来回抖动。
 */
function applyBarycenter(
  layers: Map<number, CanvasNode[]>,
  sortedLayerKeys: number[],
  outgoing: Map<string, string[]>,
  incoming: Map<string, string[]>,
): void {
  for (let round = 0; round < BARYCENTER_ROUNDS; round++) {
    // 向下扫描：用上一层序号
    for (let i = 1; i < sortedLayerKeys.length; i++) {
      const layerKey = sortedLayerKeys[i];
      const prevLayerKey = sortedLayerKeys[i - 1];
      reorderLayerByBarycenter(
        layers.get(layerKey)!,
        layers.get(prevLayerKey)!,
        incoming,
      );
    }
    // 向上扫描：用下一层序号
    for (let i = sortedLayerKeys.length - 2; i >= 0; i--) {
      const layerKey = sortedLayerKeys[i];
      const nextLayerKey = sortedLayerKeys[i + 1];
      reorderLayerByBarycenter(
        layers.get(layerKey)!,
        layers.get(nextLayerKey)!,
        outgoing,
      );
    }
  }
}

/**
 * 用相邻层的节点序号计算当前层每个节点的 barycenter 并重排。
 * neighborMap: 当前层节点 → 相邻层邻居 id 列表（incoming 或 outgoing）
 */
function reorderLayerByBarycenter(
  currentLayer: CanvasNode[],
  neighborLayer: CanvasNode[],
  neighborMap: Map<string, string[]>,
): void {
  // 相邻层节点序号映射
  const orderOf = new Map<string, number>();
  neighborLayer.forEach((n, idx) => orderOf.set(n.id, idx));

  // 计算每个节点的 barycenter
  const withBary = currentLayer.map((n, originalIdx) => {
    const neighbors = neighborMap.get(n.id) || [];
    const indices = neighbors
      .map((id) => orderOf.get(id))
      .filter((v): v is number => v !== undefined);
    const bary = indices.length > 0 ? indices.reduce((a, b) => a + b, 0) / indices.length : originalIdx;
    return { node: n, bary, originalIdx };
  });

  // 稳定排序：barycenter 相同时保持原顺序
  withBary.sort((a, b) => a.bary - b.bary || a.originalIdx - b.originalIdx);

  // 写回
  for (let i = 0; i < currentLayer.length; i++) {
    currentLayer[i] = withBary[i].node;
  }
}

/** 全部是孤立节点时：纵向排列 */
function layoutIsolatedOnly(nodes: CanvasNode[]): LayoutResult[] {
  const allX = nodes.map((n) => n.x);
  const allY = nodes.map((n) => n.y);
  const minCanvasX = Math.min(...allX);
  const minCanvasY = Math.min(...allY);

  const sizeOf = (n: CanvasNode) => ({
    width: n.width || (DEFAULT_NODE_SIZES[n.type]?.width ?? 320),
    height: n.height || (DEFAULT_NODE_SIZES[n.type]?.height ?? 220),
  });

  const sorted = [...nodes].sort((a, b) => a.y - b.y);
  let curY = minCanvasY;
  const result: LayoutResult[] = [];
  for (const n of sorted) {
    result.push({ id: n.id, x: minCanvasX, y: curY });
    curY += sizeOf(n).height + GAP_Y;
  }
  return result;
}
